import { useEffect, useMemo, useRef, useState } from "react";
import { PropValidator, createEntityStore, useEntityValidation } from "../../common/entityStore";
import { CustomerFlagId, PaymentMethodId, useDeliveryMethods } from "../../common/enum";
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, interval, map } from "rxjs";
import { createListStore } from "../../common/listStore";
import { OrderTypeSelect } from "./OrderTypeSelect";
import { selectProductWhenPossible, TmProjectSelect } from "./TmProjectSelect";
import { useParams } from "react-router-dom";
import { isNumber, round, sum } from "lodash";
import { TmOrderDto, initialCustomer, ProjectDto, ProductDto, TmOrderType, CustomerDto, ArticleCategoryDto, getInitialOrderData, setMailOrder, setContOrder, getMailProduct, getContProducts, getInitialCustomerData } from "./common";
import { retryButton, useDialog, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { useStateWithDeps } from "use-state-with-deps";
import { fromToday, getLanguages, newId, today, useRxJsSub } from "../../common/utils";
import { AxiosError, CanceledError } from "axios";
import dayjs from "dayjs";
import shallow from "zustand/shallow";
import { createAxios, getErrorCodeText, getErrorMessage } from "../../common/axios";
import { useDefaultValues } from "../../common/defaultValues";
import { propLength, propValidValue, propValueValidator, propIf, propRequired, propMinValue } from "../../common/entityStoreValidators";
import { getCustomerAddressBaseValidators } from "../customer/validators";
import { OrderCalculation, getOrderCalculation, mailOrderArticlesError } from "../../common/order";
import { OrderCreatedDialog } from "./OrderCreatedDialog";
import { Alert, Backdrop, Button, CircularProgress, Grid } from "@mui/material";
import { Customer, okPhoneStopIds } from "./Customer";
import { MailOrderPostageAndGifts } from "./MailOrderPostageAndGifts";
import { Order } from "./Order";
import { formatNumber, getLocalizedCurrency, getResource, useResources, useResourcesStore } from "../../common/localization";
import { TmCallResultDialog } from "./TmCallResultDialog";
import { ProjectMailOrderConditionDto } from "../../common/types";
import { useAccountStore } from "../../common/account";
import { PaymentLinkCreatedDialog } from "./PaymentLinkCreatedDialog";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui." + key, defaultMessage) as string;
}

export function TmOrderPage() {
    const urlParams = useParams();
    const resources = useResources();
    return resources.ready ? <TmOrderPageInternal key={urlParams.id} /> : <></>;
}

export function TmOrderPageInternal() {
    const urlParams = useParams();
    const def = useDefaultValues(p => p.clientData);
    const { custId, ordId } = useMemo(() => {
        if (urlParams.id == null || urlParams.id === "new") {
            return {};
        }
        const [custId, ordId] = urlParams.id.split("-");
        if (!isNumber(parseInt(custId)) || !isNumber(parseInt(ordId))) {
            return {};
        }
        return { custId, ordId: parseInt(ordId) };
    }, [urlParams.id]);
    const readOnly = custId != null && ordId != null;
    function getInitialData() {
        return {
            requestId: new Date().toISOString() + newId().toString(),
            ...getInitialOrderData(),
            isCustomerLoaded: false,
            isValidatingCustomer: true,
            ...initialCustomer,
            custId: custId ?? "",
            ordId: ordId,
            id: null,
            customerId: null,
            isTelInvalid: false,
            isMobInvalid: false,
            isEntitySelected: false,
            leadEntityId: "",
            ...getInitialCustomerData(),
            interest: "",
            createPaymentLink: false
        };
    }
    const paymentLinkCreatedDialog = useDialog(close => <PaymentLinkCreatedDialog order={useStore.getState().serverData} close={close} useOrderStore={useStore} />, "sm");
    const orderCreatedDialog = useDialog(close => <OrderCreatedDialog order={useStore.getState().serverData} close={close} useOrderStore={useStore} />, "sm");
    const [useStore] = useState(() => {
        const useStore = createEntityStore<TmOrderDto>({
            data: getInitialData(), path: "tm/orders", id: readOnly ? `${custId}-${ordId}` : null,
            isValidationEnabled: !readOnly ? true : undefined,
            saveAttemptLimit: 3,
            handleSaveError: async error => {
                const content = getText("Order.SaveError.Content", "An error occurred while saving. Please try again.\nDetails: {error}")
                    .replaceAll("{error}", await getErrorMessage(error) + getErrorCodeText(error));
                simpleDialog.show(getText("Order.SaveError.Title", "Save error"), content, undefined, retryButton(() => {
                    // Explicit retry: always ignore isSaveDisabled (not-interested also uses forceEnabled on first click).
                    useStore.getState().saveEx(true);
                }), true);
            },
            async onSaved(entity) {
                if (entity.notInterested) {
                    reset(entity);
                } 
                else if (entity.tmPaymentLink) {
                    showPaymentLinkStatusInfo(entity);
                }
                else {
                    orderCreatedDialog.show(() => {
                        reset(entity);
                    }, true);
                }
            },
            onFetched: () => updateOrder(),
            checkDataBeforeSave,
        });

        function showPaymentLinkStatusInfo(entity: TmOrderDto) {
            paymentLinkCreatedDialog.show(() => {
                reset(entity);
            }, true);
        }

        function reset(entity: TmOrderDto) {
            const initialData = getInitialData();
            useStore.getState().setServerData(initialData);
            useStore.getState().setClientData(initialData);
            useStore.getState().setClientDataProp("projectCodeAndName", entity.projectCodeAndName);
            useStore.getState().setClientDataProp("moProjectId", entity.moProjectId);
            if (entity.tmOrderType === TmOrderType.Mail) {
                setMailOrder(useStore, getMailProduct(entity.projectCodeAndName!, useProjectsStore)!, false);
            } else if (entity.tmOrderType === TmOrderType.Continuous) {
                setContOrder(useStore, getContProducts(entity.projectCodeAndName!, useProjectsStore), false);
            }
            updateOrder();
        }

        return useStore;
    });
    useRxJsSub(() => interval(10_000).subscribe(async () => {
        const data = useStore.getState().clientData;
        if (readOnly && useDefaultValues.getState().clientData.isTmOrderConfirmationRequired && !data.confirmedByCustomer && data.isTmOrder && data.isSameCallCenter) {
            const response = await createAxios().get<boolean>(`tm/orders/${custId}-${ordId}/confirmedByCustomer`);
            useStore.getState().setClientDataProp("confirmedByCustomer", response.data);
        }
    }));

    const [product$] = useState(() => new BehaviorSubject<ProductDto | undefined>(undefined));
    useRxJsSub(() => combineLatest([
        useProductsStore.getState().items$,
        useStore.getState().clientData$.pipe(map(p => ({
            tmOrderType: p.data.tmOrderType,
            projectId: p.data.projectId,
            moProjectId: p.data.moProjectId,
        })), distinctUntilChanged(shallow))
    ]).pipe(map(([products, p]) => {
        return p.tmOrderType == null || (p.tmOrderType === TmOrderType.Continuous && p.projectId == null) ? undefined :
            products.items.find(p.tmOrderType === TmOrderType.Mail ? r => r.projectId === p.moProjectId : r => r.projectId === p.projectId);
    })).subscribe(product$));

    const tmCallResultDialogResolveRef = useRef<(result: boolean) => void>(() => () => false);
    const tmCallResultDialog = useDialog(close => <TmCallResultDialog close={close} useStore={useStore} resolve={tmCallResultDialogResolveRef.current} />, "sm");
    async function checkDataBeforeSave() {
        function askForFix(title: string, message: string) {
            return new Promise<"dataIsValid" | "cannotFix" | "cancel">(resolve => simpleDialog.show(title, message, undefined, (close: () => void): JSX.Element => <>
                <Button onClick={() => { resolve("cannotFix"); close(); }} color="negative">{getResource("Ui.CannotFix")}</Button>
                <Button onClick={() => { resolve("dataIsValid"); close(); }} color="warning">{getResource("Ui.DataIsValid")}</Button>
                <Button onClick={() => { resolve("cancel"); close(); }}>{getResource("Ui.FixInvalidData")}</Button>
            </>, true));
        }
        function askForStopFix(title: string, message: string) {
            return new Promise<"cannotFix" | "cancel">(resolve => simpleDialog.show(title, message, undefined, (close: () => void): JSX.Element => <>
                <Button onClick={() => { resolve("cannotFix"); close(); }} color="negative">{getResource("Ui.CannotFix")}</Button>
                <Button onClick={() => { resolve("cancel"); close(); }}>{getResource("Ui.FixInvalidData")}</Button>
            </>, true));
        }
        function askForStopYesFix(title: string, message: string) {
            return new Promise<"confirm" | "cancel">(resolve => simpleDialog.show(title, message, undefined, (close: () => void): JSX.Element => <>
                <Button onClick={() => { resolve("confirm"); close(); }} color="warning">{getResource("Ui.Confirm")}</Button>
                <Button onClick={() => { resolve("cancel"); close(); }}>{getResource("Ui.Cancel")}</Button>
            </>, true));
        }
        function askForEmailFix() {
            return new Promise<"cannotFix" | "cancel">(resolve => simpleDialog.show(getText("Customer.FixEmail.Title"), getText("Customer.FixEmail.Content"), undefined, (close: () => void): JSX.Element => <>
                <Button onClick={() => { resolve("cannotFix"); close(); }} color="negative">{getResource("Ui.CannotFix")}</Button>
                <Button onClick={() => { resolve("cancel"); close(); }}>{getResource("Ui.FixInvalidData")}</Button>
            </>, true));
        }

        if (useStore.getState().clientData.mailStopId === CustomerFlagId.MailStop_Yes &&
            !useStore.getState().clientData.mailStopIdOk) {
            switch (await askForStopYesFix(getText("Customer.FixMailStopYes.Title"), getText("Customer.FixMailStopYes.Content"))) {
                case "confirm":
                    useStore.getState().setClientDataProp("mailStopIdOk", true);
                    break;
                case "cancel":
                    return false;
            }
        } else if (useStore.getState().clientData.mailStopId !== CustomerFlagId.MailStop_GdprOk &&
            !useStore.getState().clientData.mailStopIdOk) {
            switch (await askForStopFix(getText("Customer.FixMailStop.Title"), getText("Customer.FixMailStop.Content"))) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("mailStopIdOk", true);
                    break;
                case "cancel":
                    return false;
            }
        }

        if (useStore.getState().clientData.emailStopId === CustomerFlagId.EmailStop_Yes &&
            !useStore.getState().clientData.emailStopIdOk) {
            switch (await askForStopYesFix(getText("Customer.FixEmailStopYes.Title"), getText("Customer.FixEmailStopYes.Content"))) {
                case "confirm":
                    useStore.getState().setClientDataProp("emailStopIdOk", true);
                    break;
                case "cancel":
                    return false;
            }
        } else if (useStore.getState().clientData.emailStopId !== CustomerFlagId.EmailStop_GdprOk &&
            !useStore.getState().clientData.emailStopIdOk) {
            switch (await askForStopFix(getText("Customer.FixEmailStop.Title"), getText("Customer.FixEmailStop.Content"))) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("emailStopIdOk", true);
                    break;
                case "cancel":
                    return false;
            }
        }

        if (useStore.getState().clientData.phoneStopId === CustomerFlagId.PhoneStop_Yes &&
            !useStore.getState().clientData.phoneStopIdOk) {
            switch (await askForStopYesFix(getText("Customer.FixPhoneStopYes.Title"), getText("Customer.FixPhoneStopYes.Content"))) {
                case "confirm":
                    useStore.getState().setClientDataProp("phoneStopIdOk", true);
                    break;
                case "cancel":
                    return false;
            }
        } else if (!okPhoneStopIds.includes(useStore.getState().clientData.phoneStopId) &&
            !useStore.getState().clientData.phoneStopIdOk) {
            switch (await askForStopFix(getText("Customer.FixPhoneStop.Title"), getText("Customer.FixPhoneStop.Content"))) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("phoneStopIdOk", true);
                    break;
                case "cancel":
                    return false;
            }
        }

        if (useStore.getState().clientData.mailStatusId === CustomerFlagId.MailStatus_WrongAddress &&
            !useStore.getState().clientData.mailStatusIdOk) {
            switch (await askForFix(getText("Customer.FixWrongAddress.Title"), getText("Customer.FixWrongAddress.Content"))) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("mailStatusIdOk", true);
                    break;
                case "dataIsValid":
                    useStore.getState().setClientDataProp("mailStatusIdOk", true);
                    useStore.getState().setClientDataProp("mailStatusId", CustomerFlagId.MailStatus_None);
                    break;
                case "cancel":
                    return false;
            }
        }

        if (useStore.getState().clientData.emailStatusId === CustomerFlagId.EmailStatus_WrongEmail &&
            !useStore.getState().clientData.emailStatusIdOk) {
            switch (await askForFix(getText("Customer.FixWrongEmail.Title"), getText("Customer.FixWrongEmail.Content"))) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("emailStatusIdOk", true);
                    break;
                case "dataIsValid":
                    useStore.getState().setClientDataProp("emailStatusIdOk", true);
                    useStore.getState().setClientDataProp("emailStatusId", CustomerFlagId.EmailStatus_None);
                    break;
                case "cancel":
                    return false;
            }
        }

        if (useStore.getState().clientData.telStatusId === CustomerFlagId.TelStatus_WrongNumber &&
            !useStore.getState().clientData.telStatusIdOk) {
            switch (await askForFix(getText("Customer.FixWrongTel.Title"), getText("Customer.FixWrongTel.Content"))) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("telStatusIdOk", true);
                    break;
                case "dataIsValid":
                    useStore.getState().setClientDataProp("telStatusIdOk", true);
                    useStore.getState().setClientDataProp("telStatusId", CustomerFlagId.TelStatus_None);
                    break;
                case "cancel":
                    return false;
            }
        }

        if (useStore.getState().clientData.mobStatusId === CustomerFlagId.MobStatus_WrongNumber &&
            !useStore.getState().clientData.mobStatusIdOk) {
            switch (await askForFix(getText("Customer.FixWrongMob.Title"), getText("Customer.FixWrongMob.Content"))) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("mobStatusIdOk", true);
                    break;
                case "dataIsValid":
                    useStore.getState().setClientDataProp("mobStatusIdOk", true);
                    useStore.getState().setClientDataProp("mobStatusId", CustomerFlagId.MobStatus_None);
                    break;
                case "cancel":
                    return false;
            }
        }

        if ((useStore.getState().clientData.email ?? "").trim() === "" &&
            !useStore.getState().clientData.emailEmptyOk) {
            switch (await askForEmailFix()) {
                case "cannotFix":
                    useStore.getState().setClientDataProp("emailEmptyOk", true);
                    break;
                case "cancel":
                    return false;
            }
        }

        const order = useStore.getState().clientData;
        const moCondsStore = useMoCondsStore.getState();
        if (!useStore.getState().clientData.notInterested &&
            !useStore.getState().clientData.giftsNotSelectedOk &&
            getOrderCalculation(order, { isLoaded: moCondsStore.fetched && !moCondsStore.fetching, items: moCondsStore.items }, product$.value?.maxDiscountAmount,
                useArticleCategoriesStore.getState().items.flatMap(p => p.articles), null,
                useAccountStore.getState().data?.accountData.accountInfo.callCenterId, order.langPref).gifts.areAnyGiftsUnselected) {
            if (!await ask(getText("Order.GiftsNotSelected.Title"), getText("Order.GiftsNotSelected.Content"))) {
                return false;
            } else {
                useStore.getState().setClientDataProp("giftsNotSelectedOk", true);
            }
        }

        if (useStore.getState().clientData.notInterested && useStore.getState().clientData.tmCallResultId == null) {
            if (!await new Promise<boolean>(resolve => {
                tmCallResultDialogResolveRef.current = resolve;
                tmCallResultDialog.show(() => resolve(false));
            })) {
                return false;
            }
        }

        return true;
    }
    const [useProjectsStore] = useState(() => createListStore<ProjectDto>({ path: readOnly ? undefined : "tm/projects", onFetched: updateOrder }));
    const { projectCodeAndName, tmOrderType, moProjectId, isSaving } = useStore(p => ({
        projectCodeAndName: p.clientData.projectCodeAndName,
        tmOrderType: p.clientData.tmOrderType,
        moProjectId: p.clientData.moProjectId,
        isSaving: p.isSaving,
    }));
    var [useMoCondsStore] = useState(() => createListStore<ProjectMailOrderConditionDto>({
        path: "common/projectMailOrderConditions",
        autoInitialFetch: false,
        onFetched: updateOrder,
    }));
    useRxJsSub(() => useStore.getState().clientData$.pipe(map(p => ({ moProjectId: p.data.moProjectId, tmOrderType: p.data.tmOrderType })), distinctUntilChanged(shallow)).subscribe(p => {
        if (p.moProjectId != null && p.tmOrderType === TmOrderType.Mail) {
            useMoCondsStore.getState().setQs({ projectId: p.moProjectId });
            useMoCondsStore.getState().fetch();
        } else {
            useMoCondsStore.getState().setItems([], false);
        }
    }));
    useRxJsSub(() => useProjectsStore.getState().items$.subscribe(p => {
        if (ordId == null) {
            selectProductWhenPossible(useStore, useProjectsStore);
        }
    }));
    const [tmAutos, setInfo] = useState<OrderCalculation>();
    const [useArticleCategoriesStore] = useStateWithDeps(() => createListStore<ArticleCategoryDto>({
        path: readOnly || moProjectId == null ? undefined : "tm/articleCategories", qs: {
            projectId: moProjectId,
        },
        autoSelectFirstItem: true,
    }), [moProjectId]);
    function updateOrder() {
        let order = useStore.getState().clientData;
        const salesArticles = useArticleCategoriesStore.getState().items.flatMap(p => p.articles);
        order = useStore.getState().clientData;
        let discAmt = order.discAmt ?? 0;
        if (ordId == null) {
            const moCondsStore = useMoCondsStore.getState();
            const info = getOrderCalculation(order, { isLoaded: moCondsStore.fetched && !moCondsStore.fetching, items: moCondsStore.items }, product$.value?.maxDiscountAmount, salesArticles, null, useAccountStore.getState().data?.accountData.accountInfo.callCenterId, order.langPref);
            setInfo(info);
            useStore.getState().setClientDataProp("autos", info?.autos || []);
            order = useStore.getState().clientData;
            discAmt = info.discount ?? 0;
        }

        useStore.getState().setClientDataProp("detailsAndAutos", useStore.getState().clientData.details.concat(useStore.getState().clientData.autos));
        const starterArticles = order.starterArticles ?? [];
        const items = order.autos.concat(order.details).concat(starterArticles);
        const postages = items.filter(p => p.isPost);
        const articles = items.filter(p => !p.isPost);
        const postagePrice = sum(postages.map(p => p.price * p.quantity));
        const articlePrice = sum(articles.map(p => p.price * p.quantity));

        discAmt = ordId == null ? Math.min(articlePrice, discAmt) : discAmt;
        const toPay = round(articlePrice + postagePrice - discAmt, 2);

        useStore.getState().setClientDataProp("postagePrice", round(postagePrice, 2));
        useStore.getState().setClientDataProp("articlePrice", round(articlePrice, 2));
        useStore.getState().setClientDataProp("discAmt", round(discAmt, 2));
        useStore.getState().setClientDataProp("toPay", round(toPay, 2));
        useStore.getState().setClientDataProp("tmPaymentLink", order.tmPaymentLink);
    }
    const [useProductsStore] = useState(() => createListStore<ProductDto>({
        data: combineLatest([
            useProjectsStore.getState().items$,
            useStore.getState().clientData$.pipe(map(p => p.data.projectCodeAndName), distinctUntilChanged()),
        ]).pipe(map(([projects, projectCodeAndName]) => ({
            items: projects.items.find(p => p.code + ";" + p.name === projectCodeAndName)?.products ?? [],
            isFetching: projects.isFetching,
        }))),
    }));

    const simpleDialog = useSimpleDialog();
    function ask(title: string, message: string) {
        return new Promise<boolean>(resolve => simpleDialog.show(title, message, undefined, yesNoButtons(() => resolve(true), () => resolve(false))));
    }

    function getCustomerValidators() {
        const validators: (PropValidator<TmOrderDto> | null | false | undefined)[] = [];


        validators.push(propLength("custId", 7, 8, getText("Validation.CustId.EmptyOr7Digits")));
        validators.push(...getCustomerAddressBaseValidators(def.landCode, true, useDeliveryMethods.getState().items.find(d => d.isDefault && d.code === "DPD")));
        validators.push(propValueValidator("duplicateCustomers", p => p?.length > 0 && "Duplicate customers found."));

        const languages = getLanguages(def);
        if (languages.length > 1) {
            validators.push(propRequired("langPref"));
        }

        return validators;
    }

    function updateValidators() {
        const validators: (PropValidator<TmOrderDto> | null | false | undefined)[] = [];

        const clientValidators = getCustomerValidators();
        validators.push(...clientValidators);

        const orderValidators: (false | PropValidator<TmOrderDto> | null | undefined)[] = [];
        orderValidators.push(propMinValue("toPay", 0));
        orderValidators.push(propValidValue("minShpDte", "minShpDteFormatted"));
        orderValidators.push(propValueValidator("minShpDte", p => p != null && dayjs(p).isValid() &&
            (dayjs(p).isBefore(today()) || dayjs(p).isAfter(fromToday(p => p.add(1, "month")))) &&
            getText("Validation.MinShipmentDateMaxOneMonthInFuture")));
        orderValidators.push(propIf(p => p.tmOrderType === TmOrderType.Continuous, () => propRequired("projectId")));
        orderValidators.push(propIf(p => p.tmOrderType === TmOrderType.Mail, () =>
            propValueValidator("details", (p, e) =>
                mailOrderArticlesError(p, e.autos, !!product$.value?.allowOrderWithoutArticles, getText("Validation.NoArticle")))));
        orderValidators.push(propIf(p => p.tmOrderType === TmOrderType.Mail, () =>
            propValueValidator("details", (p, e) => e.detailsErrors > 0 && getText("Validation.ArticlesContainErrors"))));
        orderValidators.push(propValueValidator("articlePrice", p => product$.value?.minOrderAmount != null && (p ?? 0) < (product$.value?.minOrderAmount ?? 0) &&
            getText("Validation.MinOrderAmount", "Minimum order amount is {0} {1}.")
                .replaceAll("{0}", formatNumber(product$.value.minOrderAmount, def.landCode, true))
                .replaceAll("{1}", getLocalizedCurrency(def.currency))));
        function canMakeOrderForDerivedProject(order: TmOrderDto) {
            const baseProjectId = product$.value?.baseProjectId;
            if (baseProjectId == null) {
                return true;
            }
            return order.orders.some(p => p.projectId === baseProjectId && p.canMakeOrderInDerivedProjects);
        }
        if (product$.value?.baseProjectId != null) {
            orderValidators.push(propRequired("custId"));
        }
        orderValidators.push(propIf(p => p.tmOrderType === TmOrderType.Mail,
            () => propValueValidator("moProjectId", (p, e) => e.custId.trim() !== "" && !canMakeOrderForDerivedProject(e) && getText("Validation.MissingOrderInBaseProject"))));
        orderValidators.push(propIf(p => p.tmOrderType === TmOrderType.Continuous,
            () => propValueValidator("projectId", (p, e) => e.custId.trim() !== "" && !canMakeOrderForDerivedProject(e) && getText("Validation.MissingOrderInBaseProject"))));

        orderValidators.push(propValueValidator("paymentMethodId", (p, e) => p === PaymentMethodId.Cod && e.deliveryMethodPlaceId != null &&
            useDeliveryMethods.getState().items.find(d => d.id === e.deliveryMethodId)?.isCodPickupPointAllowed !== true && getText("Validation.CodPickupPointNotAllowed")));

        validators.push(...orderValidators);

        useIsClientValidStore.getState().setClientValidators(clientValidators);
        useStore.getState().setPropValidators(validators);
    }
    useRxJsSub(() => useResourcesStore.getState().clientData$.subscribe(() => updateValidators()));
    useRxJsSub(() => product$.subscribe(() => updateValidators()));
    const useIsClientValidStore = useEntityValidation<TmOrderDto>(useStore);

    const customerLoadState = useRef<{ abortController: AbortController; custId: string; isLoaded: boolean; isLoading: boolean; }>();
    useRxJsSub(() => combineLatest([useStore.getState().clientData$.pipe(map(p => ({ custId: p.data.custId })), distinctUntilChanged(shallow), debounceTime(500)), useDefaultValues.getState().clientData$]).subscribe(async ([p, { data: def }]) => {
        if (ordId != null) {
            return;
        }

        const custId = p.custId?.trim();
        if (custId !== (customerLoadState.current?.custId ?? "")) {
            if ((custId.startsWith("L") && custId.length === 8) || (!custId.startsWith("L") && custId.length === 7)) {

                if (useStore.getState().clientData.isEntitySelected) {
                    useStore.getState().setClientDataProp("isEntitySelected", false);
                    return;
                }

                customerLoadState.current?.abortController.abort();
                customerLoadState.current = { abortController: new AbortController(), custId, isLoaded: false, isLoading: true };
                const state = customerLoadState.current;
                try {
                    const { data } = await createAxios().get<CustomerDto>("tm/customers/" + custId, { signal: customerLoadState.current?.abortController.signal });
                    useStore.getState().setClientData({ ...useStore.getState().clientData, ...data, isCustomerLoaded: !data.isLead, ...getInitialCustomerData() });
                    useStore.getState().setServerData({ ...useStore.getState().serverData, ...data });
                    updateValidators();
                    updateOrder();

                    if (data.isLead && data.isLeadPassive) {
                        simpleDialog.show("Info", getText("Order.Lead.IsPassive", "Customer {0} was created from lead {1}")
                            .replace("{0}", data.leadCustomerCustId)
                            .replace("{1}", data.custId),
                        );
                    }
                } catch (err: unknown) {
                    if (err instanceof AxiosError && err.response?.status === 404) {
                        useStore.getState().setClientData({ ...useStore.getState().clientData, ...initialCustomer, isCustomerLoaded: false, ...getInitialCustomerData() });
                        useStore.getState().setServerData({ ...useStore.getState().serverData, ...initialCustomer });
                        updateValidators();
                        updateOrder();
                    } else {
                        throw err;
                    }
                }
                state.isLoading = false;
                state.isLoaded = true;
            } else if (customerLoadState.current?.isLoaded === true || customerLoadState.current?.isLoading === true) {
                customerLoadState.current?.abortController.abort();
                customerLoadState.current = { abortController: new AbortController(), custId, isLoaded: false, isLoading: false };
                useStore.getState().setClientData({ ...useStore.getState().clientData, ...initialCustomer, custId, isCustomerLoaded: false, ...getInitialCustomerData() });
                useStore.getState().setServerData({ ...useStore.getState().serverData, ...initialCustomer, custId });
                updateValidators();
                updateOrder();
            }
        }
    }));
    useEffect(() => () => customerLoadState.current?.abortController.abort(), []);

    const customerDuplicityCheckState = useRef<{
        abortController: AbortController;
        isLoading: boolean;
        data: {
            mob: string;
            tel: string;
            email: string;
            fname: string;
            lname: string;
            zipCode: string;
        }
    }>();
    useRxJsSub(() => combineLatest([
        useStore.getState().clientData$.pipe(map(p => ({
            custId: p.data.custId.trim(),
            mob: p.data.mob.trim(),
            tel: p.data.tel.trim(),
            email: p.data.email.trim(),
            fname: p.data.fname.trim(),
            lname: p.data.lname.trim(),
            zipCode: p.data.zipCode.trim(),
        })), distinctUntilChanged(shallow), debounceTime(500)),
        useDefaultValues.getState().clientData$
    ]).subscribe(async ([data, { data: def }]) => {

        if (useStore.getState().clientData.isEntitySelected) {
            return;
        }

        const isLeadInput = data.custId.startsWith("L");

        if (data.custId.length !== 0 && !isLeadInput) {
            return;
        }

        if (data.mob === "" && data.tel === "" && data.email === "" && data.fname === "" && data.lname === "" && data.zipCode === "") {
            return;
        }

        customerDuplicityCheckState.current?.abortController.abort();
        customerDuplicityCheckState.current = { abortController: new AbortController(), data, isLoading: true };
        try {
            const { data: result } = await createAxios().post<CustomerDto[]>("tm/customers/search", data, { signal: customerDuplicityCheckState.current?.abortController.signal });
            useStore.getState().setClientDataProp("duplicateCustomers", result);
        } catch (err: unknown) {
            if (err instanceof CanceledError) {
                return;
            }
            throw err;
        }
    }));

    useEffect(() => () => customerDuplicityCheckState.current?.abortController.abort(), []);

    const moCondsStore = useMoCondsStore(p => ({ isLoading: p.fetching && !p.fetched }));
    const articleCategoriesStore = useArticleCategoriesStore(p => ({ isLoading: p.fetching && !p.fetched }));
    const deliveryMethods = useDeliveryMethods(p => ({ isLoading: p.fetching && !p.fetched }));
    var isLoadingData = moCondsStore.isLoading || articleCategoriesStore.isLoading || deliveryMethods.isLoading;
    return <>
        <Backdrop sx={(theme) => ({ color: '#fff ', zIndex: theme.zIndex.drawer + 1 })} open={isSaving}>
            <CircularProgress color="inherit" />
        </Backdrop>
        {simpleDialog.render()}
        {paymentLinkCreatedDialog.render()}
        {orderCreatedDialog.render()}
        {tmCallResultDialog.render()}
        <TmProjectSelect useStore={useStore} useProjectsStore={useProjectsStore} updateOrder={updateOrder} readOnly={readOnly} ask={ask} />
        {projectCodeAndName != null && tmOrderType == null && <OrderTypeSelect useStore={useStore} useProductsStore={useProductsStore} />}
        {tmOrderType == null || (tmAutos == null && ordId == null) || isLoadingData ? <></> : tmAutos?.error != null ? <>
            {tmAutos?.error && <Grid item xs={12} m={2}>
                <Alert severity="error">{tmAutos.error}</Alert>
            </Grid>}
        </> : <>
            <Grid container alignContent="stretch">
                <Grid item xs={3} border={1} container alignContent="flex-start">
                    {tmAutos && <MailOrderPostageAndGifts useStore={useStore} info={tmAutos} updateOrder={updateOrder} simpleDialog={simpleDialog} />}
                </Grid>
                <Grid item xs={6} border={1} container alignContent="flex-start" p={2}>
                    <Order useStore={useStore} useProductsStore={useProductsStore} useMoCondsStore={useMoCondsStore} updateOrder={updateOrder}
                        readOnly={readOnly} useArticleCategoriesStore={useArticleCategoriesStore} ask={ask} simpleDialog={simpleDialog}
                        useIsClientValidStore={useIsClientValidStore} product$={product$} />
                </Grid>
                <Grid item xs={3} border={1} container alignContent="flex-start" p={2}>
                    <Customer useStore={useStore} readOnly={readOnly} ask={ask} updateOrder={updateOrder} />
                </Grid>
            </Grid>
        </>}
    </>;
}
